import { useEffect, useState } from "react";
import { api } from "./api";
import { useBranding } from "./branding";
import { useMediaPlayer } from "./player";

const POLL_MS = 5_000;

/**
 * Shared hook for the live-radio player state.
 *
 * Owns:
 *  - the now-playing meta (polled every 5s)
 *  - the resolved stream URL (live feed audio_url > admin radio.stream_url)
 *  - on-air boolean
 *  - a toggle() function that starts/pauses the stream via the global MiniPlayer
 *
 * Consumed by ListenLive widget AND the header LISTEN LIVE button so they
 * stay in sync.
 */
export function useLiveRadio() {
    const { logoUrl, radio } = useBranding();
    const { playItem, current, playing, toggle } = useMediaPlayer();
    const [meta, setMeta] = useState(null);

    useEffect(() => {
        let cancelled = false;
        let lastFp = "";
        const tick = async () => {
            try {
                const r = await api.get("/radio/now-playing");
                if (cancelled) return;
                const d = r.data || {};
                const fp = `${d.source || ""}|${d.song || ""}|${d.artist || ""}|${d.album || ""}|${d.audio_url || ""}|${d.on_air}`;
                if (fp !== lastFp) {
                    lastFp = fp;
                    setMeta(d);
                }
            } catch {
                /* keep last known */
            }
        };
        tick();
        const id = setInterval(tick, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const liveAudioUrl = (meta?.audio_url || "").trim();
    const streamUrl = liveAudioUrl || radio?.stream_url || "";
    const hasStream = !!streamUrl;
    const onAir = !!meta?.on_air && hasStream;

    const isThisStreamCurrent = current?.id === "radio-live";
    const isPlaying = isThisStreamCurrent && playing;

    const togglePlay = () => {
        if (!hasStream) return;
        if (isThisStreamCurrent) {
            toggle();
            return;
        }
        const song = (meta?.song || "").trim();
        const artist = (meta?.artist || "").trim();
        const title = song || artist || "MyStreetz Radio";
        const album = (meta?.album || "").trim();
        const subtitle = [artist && song ? artist : "", album].filter(Boolean).join(" · ") || "MyStreetz Radio";
        playItem({
            id: "radio-live",
            title,
            host: subtitle,
            image: logoUrl,
            url: streamUrl,
        });
    };

    return { meta, onAir, hasStream, isPlaying, togglePlay, streamUrl };
}
